/** @jsxImportSource @emotion/react */
import { usePhotoUriForLayoutSize } from '@kaiizen/kai-pro-api-react-state';
import { type ReactNode, useEffect, useMemo, useRef } from 'react';

import {
  getActivityLogEntrySortMs,
  type VideoToolboxActivityLogEntry,
} from './videoToolboxActivityLogLib';
import type { VideoToolboxChatStyles } from './videoToolboxChatStyles';
import { DEFAULT_VIDEO_TOOLBOX_CHAT_STYLES } from './videoToolboxChatStyles';
import type { VideoToolboxProposalPhotoCandidate } from './videoToolboxProposalPhotoLib';
import {
  formatVideoToolboxThreadCreatedAt,
  type VideoToolboxThreadHistoryEntry,
} from './videoToolboxThreadHistoryLib';
import { VideoToolboxThreadIntro } from './VideoToolboxThreadIntro';
import {
  parseVoiceScopeActPresentation,
  parseVoiceScopeDismissedPresentation,
  type VoiceScopeActPath,
} from './videoToolboxVoiceActLib';

export type VideoToolboxChatProps = {
  entries: VideoToolboxActivityLogEntry[];
  historyEntries?: VideoToolboxThreadHistoryEntry[];
  clipStillsCount?: number;
  isThinking?: boolean;
  pendingProposalId?: string;
  proposalPhotoCandidates?: readonly VideoToolboxProposalPhotoCandidate[];
  selectedProposalPhotoId?: string;
  onSelectProposalPhotoId?: (photoId: string | undefined) => void;
  onConfirmProposal?: () => void;
  onDismissProposal?: () => void;
  styles?: VideoToolboxChatStyles;
};

const sortThreadEntries = (entries: VideoToolboxActivityLogEntry[]): VideoToolboxActivityLogEntry[] =>
  entries
    .slice()
    .sort((left, right) => getActivityLogEntrySortMs(left) - getActivityLogEntrySortMs(right));

type VoiceScopeActPathProps = {
  path: VoiceScopeActPath;
  styles: VideoToolboxChatStyles;
};

const VoiceScopeActPathRow = ({ path, styles }: VoiceScopeActPathProps) => (
  <div css={styles.actPath} className={'v2g-act-path'}>
    {path.where && (
      <>
        <span css={styles.actPathWhere} className={'v2g-act-where'}>{path.where}</span>
        <span css={styles.actPathSep} className={'v2g-ap-sep'}>{'\u203a'}</span>
      </>
    )}
    <span css={styles.actPathWhat} className={'v2g-act-what'}>{path.what}</span>
    {path.how && (
      <>
        <span css={styles.actPathSep} className={'v2g-ap-sep'}>{'\u203a'}</span>
        <span css={styles.actPathHow} className={'v2g-act-how'}>{path.how}</span>
      </>
    )}
  </div>
);

type VideoToolboxHistoryRowShellProps = {
  createdAtMs: number;
  styles: VideoToolboxChatStyles;
  children: ReactNode;
};

const VideoToolboxHistoryRowShell = ({
  createdAtMs,
  styles,
  children,
}: VideoToolboxHistoryRowShellProps) => (
  <div css={styles.historyRowShell} className={'v2g-history-row'}>
    {children}
    <span css={styles.historyCreatedAt} className={'v2g-history-at'} data-testid={'video-toolbox-history-created-at'}>
      {formatVideoToolboxThreadCreatedAt(createdAtMs)}
    </span>
  </div>
);

/** A thread photo loaded at the size its square `cover` tile needs (a 4:3 photo's long edge spans tile side × 4/3). */
const VideoToolboxPhotoThumb = ({ photoId, tileSizePx }: { photoId: string; tileSizePx: number }) => {
  const photoUri = usePhotoUriForLayoutSize(photoId, Math.ceil(tileSizePx * 4 / 3));
  return photoUri ? <img src={photoUri} alt={''} /> : null;
};

type VoiceScopeProposalPhotoPickerProps = {
  candidates: readonly VideoToolboxProposalPhotoCandidate[];
  selectedPhotoId?: string;
  onSelectPhotoId?: (photoId: string | undefined) => void;
  styles: VideoToolboxChatStyles;
};

const VoiceScopeProposalPhotoPicker = ({
  candidates,
  selectedPhotoId,
  onSelectPhotoId,
  styles,
}: VoiceScopeProposalPhotoPickerProps) => {
  if (!candidates.length) return null;

  return (
    <div css={styles.actPhotoPicker} className={'v2g-act-photos'} data-testid={'video-toolbox-proposal-photo-picker'}>
      <span css={styles.actPhotoPickerLabel}>Attach a recent photo</span>
      <div css={styles.actPhotoOptions} className={'v2g-act-photo-opts'} role={'listbox'} aria-label={'Recent group photos'}>
        {candidates.map(candidate => {
          const isSelected = selectedPhotoId === candidate.photoId;
          return (
            <button
              key={candidate.photoId}
              type={'button'}
              css={[styles.actPhotoOption, isSelected && styles.actPhotoOptionSelected]}
              className={['v2g-act-photo-opt', isSelected ? 'is-selected' : ''].filter(Boolean).join(' ')}
              aria-pressed={isSelected}
              data-testid={`video-toolbox-proposal-photo-${candidate.photoId}`}
              onClick={() => onSelectPhotoId?.(isSelected ? undefined : candidate.photoId)}
            >
              <span css={styles.actPhotoThumb} className={'v2g-act-photo-thumb'}>
                <VideoToolboxPhotoThumb photoId={candidate.photoId} tileSizePx={52} />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

type VoiceScopeActRowProps = {
  summary: string;
  state: 'pending' | 'applied';
  showActions?: boolean;
  proposalPhotoCandidates?: readonly VideoToolboxProposalPhotoCandidate[];
  selectedProposalPhotoId?: string;
  onSelectProposalPhotoId?: (photoId: string | undefined) => void;
  onConfirmProposal?: () => void;
  onDismissProposal?: () => void;
  styles: VideoToolboxChatStyles;
  testId: string;
};

type VoiceScopeDismissedActRowProps = {
  reason: string;
  utterance: string;
  styles: VideoToolboxChatStyles;
  testId: string;
};

const VoiceScopeDismissedActRow = ({
  reason,
  utterance,
  styles,
  testId,
}: VoiceScopeDismissedActRowProps) => {
  const presentation = parseVoiceScopeDismissedPresentation(reason);

  return (
    <div
      css={[styles.actBlock, styles.actBlockDismissed]}
      className={'v2g-msg is-kai v2g-act is-dismissed'}
      data-testid={testId}
    >
      <div css={styles.actTop} className={'v2g-act-top'}>
        <span css={styles.actLead} className={'v2g-act-lead'}>{presentation.lead}</span>
      </div>
      <div css={styles.actPath} className={'v2g-act-path'}>
        <span css={styles.actPathWhat} className={'v2g-act-what'}>{presentation.body}</span>
      </div>
      {!reason.includes(utterance) && utterance && <span css={styles.actMeta}>Heard: &quot;{utterance}&quot;</span>}
    </div>
  );
};

const VoiceScopeActRow = ({
  summary,
  state,
  showActions = false,
  proposalPhotoCandidates = [],
  selectedProposalPhotoId,
  onSelectProposalPhotoId,
  onConfirmProposal,
  onDismissProposal,
  styles,
  testId,
}: VoiceScopeActRowProps) => {
  const presentation = parseVoiceScopeActPresentation(summary);
  const lead = state === 'pending' ? presentation.pendingLead : presentation.appliedLead;

  return (
    <div
      css={[
        styles.actBlock,
        state === 'pending' ? styles.actBlockPending : styles.actBlockApplied,
      ]}
      className={[
        'v2g-msg',
        'is-kai',
        'v2g-act',
        state === 'pending' ? 'is-pending' : 'is-applied',
        `is-${presentation.utype}`,
      ].join(' ')}
      data-testid={testId}
    >
      <div css={styles.actTop} className={'v2g-act-top'}>
        <span css={styles.actLead} className={'v2g-act-lead'}>{lead}</span>
      </div>
      <VoiceScopeActPathRow path={presentation.path} styles={styles} />
      {showActions && (
        <VoiceScopeProposalPhotoPicker
          candidates={proposalPhotoCandidates}
          selectedPhotoId={selectedProposalPhotoId}
          onSelectPhotoId={onSelectProposalPhotoId}
          styles={styles}
        />
      )}
      {showActions && (
        <div css={styles.actButtons} className={'v2g-act-btns'}>
          <button
            type={'button'}
            css={styles.actButtonYes}
            className={'v2g-act-y'}
            data-testid={'video-toolbox-confirm-proposal'}
            onClick={onConfirmProposal}
          >
            Apply
          </button>
          <button
            type={'button'}
            css={styles.actButtonNo}
            className={'v2g-act-n'}
            data-testid={'video-toolbox-dismiss-proposal'}
            onClick={onDismissProposal}
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
};

/** Chronological Ask Kai thread under the walkthrough video. */
const VideoToolboxPhotoRow = ({
  message,
  photoId,
  styles,
  testId,
}: {
  message: string;
  photoId: string;
  styles: VideoToolboxChatStyles;
  testId: string;
}) => (
  <div
    css={styles.shotRow}
    className={'v2g-msg is-kai v2g-shot'}
    data-testid={testId}
  >
    <span css={styles.shotImage} className={'v2g-shot-img'}>
      <VideoToolboxPhotoThumb photoId={photoId} tileSizePx={44} />
    </span>
    <span css={styles.shotText} className={'v2g-shot-t'}>{message}</span>
  </div>
);

export const VideoToolboxChat = ({
  entries,
  historyEntries = [],
  clipStillsCount = 0,
  isThinking = false,
  pendingProposalId,
  proposalPhotoCandidates = [],
  selectedProposalPhotoId,
  onSelectProposalPhotoId,
  onConfirmProposal,
  onDismissProposal,
  styles = DEFAULT_VIDEO_TOOLBOX_CHAT_STYLES,
}: VideoToolboxChatProps) => {
  const threadRef = useRef<HTMLDivElement>(null);
  const threadEntries = useMemo(() => sortThreadEntries(entries), [entries]);

  useEffect(() => {
    const thread = threadRef.current;
    if (!thread) return;
    thread.scrollTop = thread.scrollHeight;
  }, [historyEntries.length, threadEntries.length, isThinking]);

  return (
    <div css={styles.chat} className={'v2g-chat'} data-testid={'video-toolbox-chat'}>
      <div
        ref={threadRef}
        css={styles.thread}
        className={'v2g-thread'}
        data-testid={'video-toolbox-chat-thread'}
      >
        <VideoToolboxThreadIntro clipStillsCount={clipStillsCount} styles={styles} />
        {historyEntries.map(entry => {
          if (entry.kind === 'history-photo') {
            return (
              <VideoToolboxHistoryRowShell key={entry.id} createdAtMs={entry.createdAtMs} styles={styles}>
                <VideoToolboxPhotoRow
                  message={entry.message}
                  photoId={entry.photoId}
                  styles={styles}
                  testId={'video-toolbox-chat-history-photo'}
                />
              </VideoToolboxHistoryRowShell>
            );
          }

          return (
            <VideoToolboxHistoryRowShell key={entry.id} createdAtMs={entry.createdAtMs} styles={styles}>
              <VoiceScopeActRow
                summary={entry.summary}
                state={'applied'}
                styles={styles}
                testId={'video-toolbox-chat-history-applied'}
              />
            </VideoToolboxHistoryRowShell>
          );
        })}
        {threadEntries.map(entry => {
          if (entry.kind === 'speech') {
            return (
              <div
                key={entry.id}
                css={styles.msgYou}
                className={'v2g-msg is-you'}
                data-testid={'video-toolbox-chat-user-message'}
              >
                {entry.text}
              </div>
            );
          }

          if (entry.kind === 'voice-proposed') {
            const showActions = pendingProposalId === entry.proposalId;
            return (
              <VoiceScopeActRow
                key={entry.id}
                summary={entry.summary}
                state={'pending'}
                showActions={showActions}
                proposalPhotoCandidates={proposalPhotoCandidates}
                selectedProposalPhotoId={selectedProposalPhotoId}
                onSelectProposalPhotoId={onSelectProposalPhotoId}
                onConfirmProposal={onConfirmProposal}
                onDismissProposal={onDismissProposal}
                styles={styles}
                testId={'video-toolbox-chat-voice-proposed'}
              />
            );
          }

          if (entry.kind === 'voice-rejected') {
            return (
              <VoiceScopeDismissedActRow
                key={entry.id}
                reason={entry.reason}
                utterance={entry.utterance}
                styles={styles}
                testId={'video-toolbox-chat-voice-rejected'}
              />
            );
          }

          if (entry.kind === 'speech-unsupported') {
            return (
              <div
                key={entry.id}
                css={styles.msgKai}
                className={'v2g-msg is-kai'}
                data-testid={'video-toolbox-chat-speech-unsupported'}
              >
                {entry.message}
              </div>
            );
          }

          return null;
        })}
        {isThinking && (
          <div css={styles.typing} className={'v2g-typing'} data-testid={'video-toolbox-thinking'}>
            <span css={styles.typingDot} />
            <span css={styles.typingDot} />
            <span css={styles.typingDot} />
          </div>
        )}
      </div>
    </div>
  );
};
